/**
 * ONE "app resumed" signal for every background re-check (KAN-1044, RD-PERF-011).
 *
 * visibilitychange + focus + pageshow (which fire together on iOS) are deduped
 * into a single event, emitted only after the app was away (hidden/blurred) for
 * at least `RESUME_AWAY_MS`. Hooks that used to keep their own listeners (each
 * with its own throttle) subscribe here instead; the realtime / ping channels
 * stay the live path, this is the ONE backup read.
 *
 * Away is stamped on visibility->hidden, pagehide and window blur; any
 * visible/focus/pageshow then consumes it, so a pair of events yields at most
 * one emit (and a short hide yields none).
 */
export const RESUME_AWAY_MS = 60 * 1000;
/** Events within this window of the last emit are the same resume. */
export const RESUME_DEDUPE_MS = 1000;

const listeners = new Set();
let awaySince = null;
let lastEmitAt = 0;
let installed = false;

function markAway() {
  if (awaySince == null) awaySince = Date.now();
}

function emit() {
  lastEmitAt = Date.now();
  Array.from(listeners).forEach((fn) => {
    try { fn(); } catch { /* one subscriber never blocks the others */ }
  });
}

function onBack() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  const since = awaySince;
  awaySince = null;
  if (since == null) return;
  const now = Date.now();
  if (now - since < RESUME_AWAY_MS) return;
  if (now >= lastEmitAt && now - lastEmitAt < RESUME_DEDUPE_MS) return;
  emit();
}

function onVisibility() {
  if (document.visibilityState === 'hidden') markAway();
  else onBack();
}

function install() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  awaySince = document.visibilityState === 'hidden' ? Date.now() : null;
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', markAway);
  window.addEventListener('blur', markAway);
  window.addEventListener('focus', onBack);
  window.addEventListener('pageshow', onBack);
}

function uninstall() {
  if (!installed) return;
  installed = false;
  awaySince = null;
  lastEmitAt = 0;
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', markAway);
  window.removeEventListener('blur', markAway);
  window.removeEventListener('focus', onBack);
  window.removeEventListener('pageshow', onBack);
}

/** @param {() => void} fn @returns {() => void} unsubscribe */
export function subscribeAppResume(fn) {
  listeners.add(fn);
  install();
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) uninstall();
  };
}

/** Test seam: fire a resume to every subscriber without touching the DOM. */
export function emitAppResumeForTest() { emit(); }
